import {
  CheckoutElementsProvider,
  PaymentElement,
  useCheckoutElements,
} from '@stripe/react-stripe-js/checkout';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBillingProfile } from '../../../entities/billing-profile';
import {
  paymentMethodErrorMessage,
  useCardSetup,
  useSyncPaymentMethods,
} from '../../../entities/payment-method';
import { isTestMode, stripe } from '../../../shared/api/stripe';
import { Button } from '../../../shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../shared/ui/dialog';
import { ErrorState } from '../../../shared/ui/error-state';
import { Notice } from '../../../shared/ui/notice';
import { Skeleton } from '../../../shared/ui/skeleton';
import { toast } from '../../../shared/ui/sonner';

/**
 * Stores a card for the organization without taking a payment: a Checkout Session in setup mode driving
 * Stripe's Payment Element, the same provider the pay page uses. Card details live in Stripe's iframe only. Once
 * Stripe has the card the mirror is refreshed here; the webhook does the same if this dialog never gets that far.
 */
export function AddCardDialog({
  organizationId,
  open,
  onOpenChange,
}: {
  organizationId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation('org');
  const setup = useCardSetup(organizationId);
  const profile = useBillingProfile(organizationId, open);
  const { mutate: start, reset } = setup;

  // A session per attempt: it is opened when the dialog opens and thrown away when it closes.
  useEffect(() => {
    if (open) start();
    else reset();
  }, [open, start, reset]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('settings.billing.cards.addTitle')}</DialogTitle>
          <DialogDescription>{t('settings.billing.cards.addHelp')}</DialogDescription>
        </DialogHeader>
        {isTestMode() ? <Notice>{t('settings.billing.cards.testMode')}</Notice> : null}
        {setup.isPending ? (
          <section
            aria-busy="true"
            aria-label={t('settings.billing.cards.starting')}
            className="grid gap-3"
          >
            <Skeleton className="h-32 w-full" />
          </section>
        ) : null}
        {setup.isError ? (
          <ErrorState
            size="compact"
            title={t('settings.billing.cards.startError')}
            description={paymentMethodErrorMessage(setup.error)}
          />
        ) : null}
        {setup.isSuccess ? (
          <CheckoutElementsProvider
            stripe={stripe()}
            options={{
              clientSecret: setup.data.clientSecret,
              // The organization's own address, so the card's country is not guessed. No email: the session's
              // customer already has one and Stripe refuses a second.
              ...(profile.data
                ? {
                    defaultValues: {
                      billingAddress: {
                        name: profile.data.legal_name,
                        address: {
                          line1: profile.data.line1,
                          line2: profile.data.line2 ?? '',
                          city: profile.data.city,
                          state: profile.data.region ?? '',
                          postal_code: profile.data.postal_code,
                          country: profile.data.country,
                        },
                      },
                    },
                  }
                : {}),
            }}
          >
            <CardFields
              organizationId={organizationId}
              onStored={() => {
                toast.success(t('settings.billing.cards.added'));
                onOpenChange(false);
              }}
              onCancel={() => onOpenChange(false)}
            />
          </CheckoutElementsProvider>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function CardFields({
  organizationId,
  onStored,
  onCancel,
}: {
  organizationId: string;
  onStored: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation('org');
  const state = useCheckoutElements();
  const sync = useSyncPaymentMethods(organizationId);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (state.type === 'loading')
    return (
      <section
        aria-busy="true"
        aria-label={t('settings.billing.cards.starting')}
        className="grid gap-3"
      >
        <Skeleton className="h-32 w-full" />
      </section>
    );
  if (state.type === 'error')
    return (
      <ErrorState
        size="compact"
        title={t('settings.billing.cards.startError')}
        description={state.error.message}
      />
    );

  const { checkout } = state;

  async function save() {
    setError(null);
    setSaving(true);
    try {
      // No `returnUrl`: the session already carries one, and Stripe refuses both.
      const result = await checkout.confirm({ redirect: 'if_required' });
      if (result.type === 'error') {
        setError(result.error.message);
        return;
      }
      await sync.mutateAsync(undefined);
      onStored();
    } catch (e) {
      setError(paymentMethodErrorMessage(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    // Not a <form>: Stripe's Payment Element swallows the submit event of an enclosing form.
    <div className="grid gap-4">
      <PaymentElement options={{ layout: 'tabs' }} />
      {error ? (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      ) : null}
      <DialogFooter>
        <Button type="button" variant="secondary" onClick={onCancel} disabled={saving}>
          {t('settings.billing.cards.cancel')}
        </Button>
        <Button
          type="button"
          variant="accent"
          disabled={!checkout.canConfirm || saving}
          onClick={() => void save()}
        >
          {saving ? t('settings.billing.cards.saving') : t('settings.billing.cards.save')}
        </Button>
      </DialogFooter>
    </div>
  );
}
